import {
  BedSingle as BedSingleIcon,
  AlertTriangle as AlertTriangleIcon,
  Droplets as DropletsIcon,
  Map as MapIcon,
  Utensils as UtensilsIcon,
  Mountain as MountainIcon,
  Tent as TentIcon,
  Anchor as AnchorIcon,
  Building as BuildingIcon,
  Bus as BusIcon,
  Car as CarIcon,
  Church as ChurchIcon,
  Fuel as FuelIcon,
  Hospital as HospitalIcon,
  Landmark as LandmarkIcon,
  ParkingSquare as ParkingSquareIcon,
  Phone as PhoneIcon,
  Plane as PlaneIcon,
  ShoppingCart as ShoppingCartIcon,
  Ship as ShipIcon,
  Signpost as SignpostIcon,
  TrainFront as TrainFrontIcon,
  Trees as TreesIcon,
  MapPin as MapPinIcon,
} from '@lucide/vue'
import { createVNode, render, type Component } from 'vue'

/**
 * Maps GPX waypoint types to corresponding Lucide icons
 * @param type The waypoint type string from GPX file
 * @returns The corresponding Lucide icon component
 */
export function getWaypointIcon(type?: string): Component {
  if (!type) return MapPinIcon

  // Normalize the type string for more reliable matching
  const normalizedType = type.toLowerCase().trim()

  // Map of common GPX waypoint types to Lucide icons
  const iconMap: Record<string, Component> = {
    // Accommodation
    accommodation: BedSingleIcon,
    lodging: BedSingleIcon,
    hotel: BedSingleIcon,
    motel: BedSingleIcon,
    shelter: BedSingleIcon,
    camp: TentIcon,
    campground: TentIcon,
    camping: TentIcon,

    // Warnings
    danger: AlertTriangleIcon,
    caution: AlertTriangleIcon,
    hazard: AlertTriangleIcon,

    // Water
    water: DropletsIcon,
    'water source': DropletsIcon,
    'potable water': DropletsIcon,
    'drinking water': DropletsIcon,
    spring: DropletsIcon,
    stream: DropletsIcon,
    river: DropletsIcon,
    lake: DropletsIcon,

    // Maps and information
    information: MapIcon,
    info: MapIcon,
    trailhead: MapIcon,
    'trail head': MapIcon,
    junction: MapIcon,

    // Food
    food: UtensilsIcon,
    restaurant: UtensilsIcon,
    cafe: UtensilsIcon,
    bar: UtensilsIcon,
    picnic: UtensilsIcon,
    'picnic area': UtensilsIcon,

    // Mountains and terrain
    summit: MountainIcon,
    peak: MountainIcon,
    mountain: MountainIcon,
    hill: MountainIcon,
    valley: MountainIcon,
    pass: MountainIcon,
    saddle: MountainIcon,

    // Nature
    forest: TreesIcon,
    park: TreesIcon,
    tree: TreesIcon,
    woods: TreesIcon,

    // Transportation
    parking: ParkingSquareIcon,
    car: CarIcon,
    auto: CarIcon,
    vehicle: CarIcon,
    transport: TrainFrontIcon,
    train: TrainFrontIcon,
    railway: TrainFrontIcon,
    station: TrainFrontIcon,
    bus: BusIcon,
    bus_stop: BusIcon,
    ferry: ShipIcon,
    ferry_terminal: ShipIcon,
    airport: PlaneIcon,
    aerodrome: PlaneIcon,
    airfield: PlaneIcon,
    helipad: PlaneIcon,
    boat: AnchorIcon,
    marina: AnchorIcon,
    harbor: AnchorIcon,
    dock: AnchorIcon,

    // Buildings and facilities
    building: BuildingIcon,
    church: ChurchIcon,
    chapel: ChurchIcon,
    gas: FuelIcon,
    fuel: FuelIcon,
    hospital: HospitalIcon,
    medical: HospitalIcon,
    'first aid': HospitalIcon,
    phone: PhoneIcon,
    telephone: PhoneIcon,
    store: ShoppingCartIcon,
    shop: ShoppingCartIcon,
    shopping: ShoppingCartIcon,
    landmark: LandmarkIcon,
    monument: LandmarkIcon,
    viewpoint: LandmarkIcon,
    scenic: LandmarkIcon,
    signpost: SignpostIcon,
    sign: SignpostIcon,
    intersection: SignpostIcon,
  }

  // Return the matching icon or the fallback
  return iconMap[normalizedType] || MapPinIcon
}

export function renderWaypointIconHtml(
  type?: string,
  options: {
    size?: number
    className?: string
    strokeWidth?: number
  } = {},
): string {
  const iconContainer = document.createElement('div')
  if (options.className) {
    iconContainer.className = options.className
  }

  const IconComponent = getWaypointIcon(type)
  const vNode = createVNode(IconComponent, {
    size: options.size ?? 16,
    stroke: 'currentColor',
    strokeWidth: options.strokeWidth ?? 2,
    absoluteStrokeWidth: true,
  })
  render(vNode, iconContainer)

  return iconContainer.innerHTML
}

/**
 * Maps Nominatim result types and categories to waypoint type strings
 * @param type The type field from Nominatim result
 * @param category The category field from Nominatim result
 * @returns The corresponding waypoint type string for icon mapping
 */
export function mapNominatimTypeToWaypointType(
  type?: string,
  category?: string,
): string | undefined {
  if (!type && !category) return undefined

  // Normalize the input strings for reliable matching
  const normalizedType = type?.toLowerCase().trim()
  const normalizedCategory = category?.toLowerCase().trim()

  // Map of Nominatim types/categories to waypoint types
  const nominatimToWaypointMap: Record<string, string> = {
    // Food & Dining
    restaurant: 'restaurant',
    cafe: 'cafe',
    bar: 'bar',
    food: 'food',
    fast_food: 'restaurant',
    pub: 'bar',

    // Accommodation
    hotel: 'hotel',
    motel: 'motel',
    hostel: 'lodging',
    guest_house: 'lodging',
    bed_and_breakfast: 'lodging',
    chalet: 'lodging',
    camp_site: 'camp',
    caravan_site: 'camping',

    // Transportation
    fuel: 'fuel',
    gas: 'fuel',
    petrol: 'fuel',
    charging_station: 'fuel',
    parking: 'parking',
    car_park: 'parking',
    railway: 'train',
    train_station: 'train',
    bus_station: 'train',
    airport: 'airport',
    aerodrome: 'airport',
    airfield: 'airport',
    helipad: 'airport',
    ferry_terminal: 'boat',
    marina: 'marina',
    harbour: 'harbor',

    // Healthcare & Emergency
    hospital: 'hospital',
    clinic: 'medical',
    pharmacy: 'medical',
    doctors: 'medical',
    dentist: 'medical',
    veterinary: 'medical',

    // Shopping & Services
    shop: 'shop',
    store: 'store',
    shopping: 'shopping',
    supermarket: 'shop',
    mall: 'shopping',
    market: 'shop',
    bank: 'building',
    atm: 'building',
    post_office: 'building',

    // Religious
    church: 'church',
    chapel: 'chapel',
    cathedral: 'church',
    mosque: 'church',
    synagogue: 'church',
    temple: 'church',

    // Nature & Recreation
    park: 'park',
    forest: 'forest',
    nature_reserve: 'park',
    beach: 'water',
    lake: 'lake',
    river: 'river',
    waterfall: 'water',
    spring: 'spring',
    mountain: 'mountain',
    peak: 'peak',
    summit: 'summit',
    viewpoint: 'viewpoint',

    // Information & Navigation
    information: 'information',
    tourist_info: 'info',
    museum: 'landmark',
    monument: 'monument',
    memorial: 'landmark',
    attraction: 'landmark',
    castle: 'landmark',
    ruins: 'landmark',

    // Utilities & Infrastructure
    telephone: 'phone',
    toilets: 'building',
    waste_disposal: 'building',
    recycling: 'building',

    // Outdoor & Sports
    campground: 'campground',
    picnic_site: 'picnic',
    shelter: 'shelter',
    alpine_hut: 'shelter',
    trail_head: 'trailhead',
    trailhead: 'trailhead',
  }

  // Check type first, then category
  const searchKey = normalizedType || normalizedCategory
  if (searchKey && nominatimToWaypointMap[searchKey]) {
    return nominatimToWaypointMap[searchKey]
  }

  // Additional category-based mappings for broader categories
  if (normalizedCategory) {
    const categoryMappings: Record<string, string> = {
      amenity: 'building', // Generic amenities
      tourism: 'landmark', // Tourist attractions
      leisure: 'park', // Leisure facilities
      shop: 'shop', // Shopping
      natural: 'forest', // Natural features
      highway: 'signpost', // Road infrastructure
    }

    if (categoryMappings[normalizedCategory]) {
      return categoryMappings[normalizedCategory]
    }
  }

  // Return undefined if no mapping found (will use default icon)
  return undefined
}
